import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import { cn } from 'lib/utils';
import { localized, TRACK_DAYS, TRACK_LENGTH, trackPosition, type TrackSkill } from 'modules/ieltsTrack';
import type { Roadmap } from 'modules/studyPlan';

/** The cell labels on the 28-day strip: the skills' own (English) names, as IELTS uses them everywhere. */
const SHORT: Record<TrackSkill, string> = {
  LISTENING: 'L',
  READING: 'R',
  WRITING_TASK1: 'W1',
  SPEAKING: 'S',
  WRITING_TASK2: 'W2',
  PRACTICE: 'T',
  REVIEW: '↻',
};

/**
 * Today's lesson on the 28-day IELTS track, for an IELTS goal: four weeks that each run every skill, a practice test
 * and a review day, laid over the goal's roadmap. Nothing while the plan has no dates or the exam has passed.
 */
export function IeltsTrackCard({ roadmap }: { roadmap: Roadmap | null }) {
  const { t, i18n } = useTranslation();
  if (!roadmap || roadmap.currentPhase === 'PAST') return null;

  const { day, week, round, lesson, next } = trackPosition(roadmap.dayIndex, roadmap.totalDays);
  const theme = t(`ieltsTrack.week_${week}`);

  return (
    <section aria-labelledby="ielts-track" className="rounded-lg border border-border bg-card p-5 md:p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h2 id="ielts-track" className="text-[15px] font-semibold text-foreground">{t('ieltsTrack.title')}</h2>
        <span className="text-[13px] text-muted-foreground">
          {t('ieltsTrack.day', { day, total: TRACK_LENGTH })}
          {round > 1 && ` · ${t('ieltsTrack.round', { round })}`}
        </span>
      </div>
      <p className="mt-1 text-[13px] text-muted-foreground">{t('ieltsTrack.week_label', { week, theme })}</p>

      <ol aria-label={t('ieltsTrack.strip_label', { done: day - 1 })} className="mt-4 grid grid-cols-7 gap-1.5">
        {TRACK_DAYS.map((d, i) => {
          const n = i + 1;
          const state = n < day ? 'done' : n === day ? 'today' : 'later';
          return (
            <li
              key={n}
              aria-current={state === 'today' ? 'step' : undefined}
              title={`${n}. ${t(`ieltsTrack.skill_${d.skill}`)}`}
              className={cn(
                'flex h-8 items-center justify-center rounded-md text-[11px] font-semibold',
                state === 'done' && 'bg-foreground/80 text-background',
                state === 'today' && 'bg-background text-foreground ring-2 ring-foreground',
                state === 'later' && 'bg-muted text-muted-foreground',
              )}
            >
              {SHORT[d.skill]}
            </li>
          );
        })}
      </ol>

      <div className="mt-5 rounded-md bg-muted/60 p-4">
        <p className="text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">
          {t('ieltsTrack.today')} · {t(`ieltsTrack.skill_${lesson.skill}`)}
        </p>
        <h3 className="mt-1 text-base font-semibold text-foreground">{localized(lesson.title, i18n.language)}</h3>
        <p className="mt-1.5 text-[13.5px] leading-relaxed text-foreground/80">{localized(lesson.body, i18n.language)}</p>
        <Link
          to="/portal/tests?category=ielts"
          className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-semibold text-foreground underline-offset-4 hover:underline focus-visible:underline"
        >
          {t('ieltsTrack.open_tests')}
          <ArrowRight size={14} aria-hidden="true" />
        </Link>
      </div>

      {next && (
        <p className="mt-3 text-[13px] text-muted-foreground">
          <span className="font-medium text-foreground/80">{t('ieltsTrack.tomorrow')}:</span>{' '}
          {t(`ieltsTrack.skill_${next.skill}`)} · {localized(next.title, i18n.language)}
        </p>
      )}
    </section>
  );
}

export default IeltsTrackCard;
